import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Droplet, Plus, Trash2, Check, Upload, Eye } from "lucide-react";

const POSITION_LABELS = {
  "top-left": "أعلى اليسار",
  "top-right": "أعلى اليمين",
  "bottom-left": "أسفل اليسار",
  "bottom-right": "أسفل اليمين",
  center: "المنتصف",
  diagonal: "قطري (افتراضي)",
};

const TARGET_LABELS = {
  pdf: "ملفات PDF",
  cad: "ملفات CAD",
  all: "الكل",
};

const BLANK_TEMPLATE = {
  name: "",
  text: "MERQAV — سري • {{factory_name}} • {{document_number}} • {{date}} • {{user_name}}",
  enabled: true,
  opacity: 0.15,
  font_size: 32,
  color: "#0A192F",
  position: "diagonal",
  logo_file_id: null,
  logo_width: 120,
  applies_to: "pdf",
  is_default: false,
};

export default function WatermarksAdmin() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [positions, setPositions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const load = useCallback(async () => {
    const [tpls, meta] = await Promise.all([
      api.get("/watermark-templates"),
      api.get("/watermark-templates/variables"),
    ]);
    setTemplates(tpls.data.templates || []);
    setVariables(meta.data.variables || []);
    setPositions(meta.data.positions || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing({ ...BLANK_TEMPLATE });
    setShowEditor(true);
  };

  const openEdit = (t) => {
    setEditing({ ...t });
    setShowEditor(true);
  };

  const updateField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  const insertVar = (key) => {
    setEditing((prev) => ({ ...prev, text: (prev.text || "") + ` {{${key}}}` }));
  };

  const requestPreview = async () => {
    try {
      const res = await api.post("/watermark-templates/preview", {
        template: {
          name: editing.name || "preview",
          text: editing.text || "",
          enabled: true,
          opacity: Number(editing.opacity),
          font_size: Number(editing.font_size),
          color: editing.color,
          position: editing.position,
          applies_to: editing.applies_to,
        },
      });
      setPreview(res.data.resolved_text);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    try {
      const payload = {
        ...editing,
        opacity: Number(editing.opacity),
        font_size: Number(editing.font_size),
        logo_width: Number(editing.logo_width || 120),
      };
      if (editing.id) {
        await api.patch(`/watermark-templates/${editing.id}`, payload);
        toast.success("تم حفظ القالب");
      } else {
        await api.post("/watermark-templates", payload);
        toast.success("تم إنشاء القالب");
      }
      setShowEditor(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد حذف هذا القالب؟")) return;
    try {
      await api.delete(`/watermark-templates/${id}`);
      toast.success("تم الحذف");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setDefault = async (t) => {
    try {
      await api.patch(`/watermark-templates/${t.id}`, { is_default: true, enabled: true });
      toast.success("تم تعيين القالب كافتراضي");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "watermark_logo");
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateField("logo_file_id", res.data.id);
      toast.success("تم رفع الشعار");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">العلامة المائية</div>
          <h1 className="text-3xl font-bold text-brand-navy">قوالب العلامة المائية</h1>
          <p className="text-slate-600 mt-1">يتم تطبيق القالب الافتراضي تلقائياً على جميع ملفات PDF التي تُنشئها أو تُحمَّل من المنصة.</p>
        </div>
        <Button className="btn-cta" onClick={openNew} data-testid="new-watermark-template-btn">
          <Plus className="w-4 h-4 me-1" /> قالب جديد
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-testid="watermark-templates-grid">
        {templates.map((t) => (
          <div key={t.id} className={`card-industrial p-5 ${t.is_default ? "border-brand-amber ring-2 ring-brand-amber/20" : ""}`} data-testid={`watermark-card-${t.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-brand-amber/10 flex items-center justify-center">
                  <Droplet className="w-5 h-5 text-brand-amber" />
                </div>
                <div>
                  <div className="font-bold text-brand-navy">{t.name}</div>
                  <div className="text-xs text-slate-500">{TARGET_LABELS[t.applies_to]} • {POSITION_LABELS[t.position]}</div>
                </div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                {t.is_default && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">افتراضي</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${t.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                  {t.enabled ? "مفعّل" : "متوقف"}
                </span>
              </div>
            </div>
            <div className="text-sm text-slate-700 mb-3 bg-slate-50 border border-slate-100 rounded-md p-3 font-mono text-xs overflow-hidden">
              {t.text}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs text-slate-500 mb-3">
              <div>الشفافية: <span className="font-semibold text-brand-navy">{Math.round((t.opacity || 0) * 100)}%</span></div>
              <div>الحجم: <span className="font-semibold text-brand-navy">{t.font_size}pt</span></div>
              <div>اللون: <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: t.color, verticalAlign: "middle" }} /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-watermark-${t.id}`}>تعديل</Button>
              {!t.is_default && (
                <Button size="sm" variant="outline" onClick={() => setDefault(t)} data-testid={`set-default-watermark-${t.id}`}>
                  <Check className="w-3 h-3 me-1" /> جعله افتراضي
                </Button>
              )}
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(t.id)} data-testid={`delete-watermark-${t.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
            لا توجد قوالب علامة مائية. أنشئ القالب الأول لبدء تطبيقه على جميع ملفات PDF.
          </div>
        )}
      </div>

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "تعديل قالب" : "قالب جديد"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>اسم القالب</Label>
                  <Input value={editing.name} onChange={(e) => updateField("name", e.target.value)} className="mt-1.5" data-testid="wm-name" />
                </div>
                <div>
                  <Label>يُطبَّق على</Label>
                  <Select value={editing.applies_to} onValueChange={(v) => updateField("applies_to", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="wm-applies-to"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">ملفات PDF</SelectItem>
                      <SelectItem value="cad">ملفات CAD</SelectItem>
                      <SelectItem value="all">الكل</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>نص العلامة المائية</Label>
                <Textarea value={editing.text} onChange={(e) => updateField("text", e.target.value)} className="mt-1.5 font-mono text-sm" rows={3} data-testid="wm-text" />
                <div className="text-xs text-slate-500 mt-2">
                  <span className="font-semibold">أدرج متغيرات ديناميكية: </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {variables.map((v) => (
                      <button key={v.key} type="button" onClick={() => insertVar(v.key)}
                        className="px-2 py-1 bg-slate-100 hover:bg-brand-amber hover:text-white rounded text-xs transition-colors"
                        data-testid={`wm-var-${v.key}`}>
                        {v.label_ar} <code className="opacity-70">{"{{"}{v.key}{"}}"}</code>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>الشفافية ({Math.round((editing.opacity || 0) * 100)}%)</Label>
                  <input type="range" min="0.05" max="1" step="0.05" value={editing.opacity}
                    onChange={(e) => updateField("opacity", Number(e.target.value))}
                    className="w-full mt-3" data-testid="wm-opacity" />
                </div>
                <div>
                  <Label>حجم الخط (pt)</Label>
                  <Input type="number" min="8" max="200" value={editing.font_size}
                    onChange={(e) => updateField("font_size", e.target.value)} className="mt-1.5" data-testid="wm-font-size" />
                </div>
                <div>
                  <Label>الموضع</Label>
                  <Select value={editing.position} onValueChange={(v) => updateField("position", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="wm-position"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(positions.length ? positions : Object.keys(POSITION_LABELS)).map((p) => (
                        <SelectItem key={p} value={p}>{POSITION_LABELS[p] || p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>اللون</Label>
                  <div className="flex gap-2 mt-1.5">
                    <input type="color" value={editing.color} onChange={(e) => updateField("color", e.target.value)}
                      className="w-12 h-9 rounded border border-slate-200 cursor-pointer" data-testid="wm-color" />
                    <Input value={editing.color} onChange={(e) => updateField("color", e.target.value)} className="flex-1 font-mono text-sm" />
                  </div>
                </div>
                <div>
                  <Label>عرض الشعار (px)</Label>
                  <Input type="number" min="40" max="400" value={editing.logo_width || 120}
                    onChange={(e) => updateField("logo_width", e.target.value)} className="mt-1.5" data-testid="wm-logo-width" />
                </div>
                <div>
                  <Label>شعار MERQAV</Label>
                  <label className="mt-1.5 flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm">
                    <input type="file" accept="image/*" onChange={onLogoUpload} className="hidden" data-testid="wm-logo-upload" />
                    <Upload className="w-4 h-4 text-brand-amber" />
                    <span>{uploading ? "جارٍ الرفع..." : editing.logo_file_id ? "تم الرفع ✓" : "اختر ملف"}</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="wm-enabled-label">
                  <Switch checked={editing.enabled} onCheckedChange={(v) => updateField("enabled", v)} data-testid="wm-enabled" />
                  <span className="text-sm">مُفعَّل</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="wm-is-default-label">
                  <Switch checked={editing.is_default} onCheckedChange={(v) => updateField("is_default", v)} data-testid="wm-is-default" />
                  <span className="text-sm">تعيين كافتراضي</span>
                </label>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold tracking-widest text-slate-500">معاينة</div>
                  <Button size="sm" variant="outline" onClick={requestPreview} data-testid="wm-preview-btn">
                    <Eye className="w-3.5 h-3.5 me-1" /> تحديث المعاينة
                  </Button>
                </div>
                {preview ? (
                  <div className="bg-white border border-dashed border-slate-300 rounded p-8 text-center overflow-hidden relative min-h-[120px]">
                    <div
                      className="inline-block font-bold whitespace-nowrap"
                      style={{
                        color: editing.color,
                        opacity: editing.opacity,
                        fontSize: `${Math.min(editing.font_size, 48)}px`,
                        transform: editing.position === "diagonal" ? "rotate(-25deg)" : "none",
                      }}
                      data-testid="wm-preview-text"
                    >
                      {preview}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">اضغط &quot;تحديث المعاينة&quot; لرؤية النص مع المتغيرات المُحلَّلة.</div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowEditor(false)}>إلغاء</Button>
                <Button className="btn-cta" onClick={save} data-testid="wm-save-btn">حفظ القالب</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
